"use client"
import { ArrowRightIcon } from "lucide-react"
import { useActionState, useRef, useCallback } from "react"
import { submitWaitlist } from "./action"
import clsx from "clsx"
import { AnimatePresence, motion } from "framer-motion"
import { X as XIcon } from "@/components/svgs/external/X"
import { useFrontsiteAnalytics } from "./useFrontsiteAnalytics"

const transition = {
  duration: 0.2,
  ease: "easeInOut",
}

export function HeroInput(props: {
  className?: string
  inputClassName?: string
  buttonVariant?: "dark" | "brand"
  successVariant?: "dark" | "light"
  inputVariant?: "dark" | "brand"
}) {
  const { onInputChange, recordWaitlistSubmission, recordWaitlistFailure } =
    useFrontsiteAnalytics()
  const submit = useCallback(
    async (previousState: any, formData: FormData) => {
      const email = formData.get("email")
      if (email) {
        recordWaitlistSubmission(email as string)
      }
      try {
        const response = await submitWaitlist(previousState, formData)
        if (!response.success) {
          recordWaitlistFailure(response?.message ?? "Unknown error")
        }
        return response
      } catch (error) {
        recordWaitlistFailure(error as string)
        return { success: false, message: "Something went wrong" }
      }
    },
    [recordWaitlistSubmission, recordWaitlistFailure],
  )
  const [state, formAction, pending] = useActionState(submit, null)
  const emailRef = useRef<string>("")

  const showEmailInput = !pending && state === null
  const showErrorMessage = !showEmailInput && state?.success === false
  const showSuccessMessage = !showEmailInput && !showErrorMessage && !pending

  const buttonVariant = props.buttonVariant ?? "brand"
  const successVariant = props.successVariant ?? "light"
  const inputVariant = props.inputVariant ?? "brand"
  return (
    <form action={formAction} className={clsx("w-full", props.className)}>
      <AnimatePresence mode="wait">
        {showEmailInput ? (
          <motion.div
            key="email-input"
            className="relative h-12 w-full font-suisse sm:max-w-96"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={transition}
          >
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              autoComplete="email"
              onChange={(e) => {
                emailRef.current = e.target.value
                onInputChange(e)
              }}
              className={clsx([
                "peer w-full rounded-full border border-white/20 px-4 py-2 pr-10 text-[16px] transition-all duration-300 focus:text-brand-black focus:outline-none focus:ring",
                inputVariant === "dark" &&
                  "text-normal bg-white/100 text-black caret-brand-black placeholder:text-black/80 active:ring-brand-yellow hover:border-black/20 focus:border-black/20 focus:shadow-sm focus:ring-transparent",
                inputVariant === "brand" &&
                  "bg-white/10 text-white caret-brand-yellow placeholder:text-white/90 hover:border-white focus:border-brand-yellow focus:bg-white/60 focus:ring-brand-yellow focus:placeholder:text-black/40",
                props.inputClassName,
              ])}
              defaultValue={emailRef.current}
            />
            <button
              type="submit"
              className={clsx([
                "absolute right-[7px] top-[7px] my-auto flex size-8 items-center justify-center rounded-full transition-all hover:scale-110 peer-focus:shadow-sm",
                buttonVariant === "brand" && "bg-brand-yellow text-black",
                buttonVariant === "dark" && "bg-brand-black text-white",
              ])}
              disabled={pending}
            >
              <ArrowRightIcon className="size-4" />
            </button>
          </motion.div>
        ) : null}
        {pending ? <div className="h-12 w-screen"></div> : null}
        {showErrorMessage ? (
          <motion.div
            key="error-message"
            className="flex h-12 w-screen flex-col gap-1 overflow-visible text-left"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={transition}
          >
            <p
              className={clsx([
                "font-alpina text-xl tracking-tight",
                successVariant === "dark" && "text-black",
                successVariant === "light" && "text-white",
              ])}
            >
              Hmm, something went wrong. Please try again.
            </p>
          </motion.div>
        ) : null}
        {showSuccessMessage ? (
          <motion.div
            key="success-message"
            className="flex h-12 w-72 flex-col gap-1 overflow-visible text-left md:w-[60vw]"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={transition}
          >
            <p
              className={clsx([
                "font-alpina text-xl tracking-tight",
                successVariant === "dark" && "text-black",
                successVariant === "light" && "text-white",
              ])}
            >
              You&apos;re on the list! Check your email for more&nbsp;info.
            </p>
            <a
              href="https://x.com/thirdpersonwork"
              target="_blank"
              className={clsx([
                "focus:ring-none group flex w-96 max-w-full flex-col justify-end rounded-xl border p-4 transition-all focus:outline-none",
                successVariant === "dark" &&
                  "border-black/20 text-black hover:border-black/80 focus:border-black/80",
                successVariant === "light" &&
                  "border-white/20 text-white hover:border-white/80 focus:border-white/80",
              ])}
            >
              <div className="flex items-center space-x-1">
                <div className="size-4 rounded-full bg-brand-green" />
                <span className="font-alpina tracking-tight">
                  Follow updates on
                </span>
                <XIcon className="size-4" />
                <ArrowRightIcon className="size-4" />
              </div>
            </a>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </form>
  )
}
